import { useState, useEffect } from 'react'
import { Plus, Building2, Home, Trash2, MapPin } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PageHeader } from '@/components/layout'
import { StatCard } from '@/components/shared'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { addProperty, removeProperty } from '@/store/slices/realEstateSlice'
import { visitPage } from '@/store/slices/gamificationSlice'
import { formatCurrency } from '@/lib/utils'
import type { PropertyType } from '@/types'

export default function RealEstate() {
  const dispatch = useAppDispatch()
  const realEstate = useAppSelector(state => state.realEstate)
  const [dialogOpen, setDialogOpen] = useState(false)

  // Track page visit for explorer achievement
  useEffect(() => {
    dispatch(visitPage('real-estate'))
  }, [dispatch])

  const [newProperty, setNewProperty] = useState({
    name: '',
    address: '',
    type: 'primary' as PropertyType,
    value: 0,
    mortgageBalance: 0,
    mortgageRate: 0,
    monthlyPayment: 0,
    rent: 0,
    rentGrowth: 3,
    expenses: 0,
    appreciation: 3,
  })

  const handleAddProperty = () => {
    if (!newProperty.name) return
    
    dispatch(addProperty(newProperty))
    
    setNewProperty({
      name: '',
      address: '',
      type: 'primary',
      value: 0,
      mortgageBalance: 0,
      mortgageRate: 0,
      monthlyPayment: 0,
      rent: 0,
      rentGrowth: 3,
      expenses: 0,
      appreciation: 3,
    })
    setDialogOpen(false)
  }

  const handleRemoveProperty = (id: number) => {
    dispatch(removeProperty(id))
  }

  const getPropertyTypeIcon = (type: PropertyType) => {
    switch (type) {
      case 'rental':
      case 'commercial':
        return <Building2 className="h-5 w-5" />
      default:
        return <Home className="h-5 w-5" />
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Real Estate" 
        description="Track property values, equity, and rental income."
      >
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Property
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add Property</DialogTitle>
              <DialogDescription>
                Add a new property to your real estate portfolio.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Property Name</Label>
                  <Input
                    placeholder="My Home"
                    value={newProperty.name}
                    onChange={(e) => setNewProperty({ ...newProperty, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={newProperty.type}
                    onValueChange={(value: PropertyType) => setNewProperty({ ...newProperty, type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="primary">Primary Residence</SelectItem>
                      <SelectItem value="rental">Rental Property</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="vacation">Vacation Home</SelectItem>
                      <SelectItem value="land">Land</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Address (Optional)</Label>
                <Input
                  placeholder="123 Main St, City, State"
                  value={newProperty.address}
                  onChange={(e) => setNewProperty({ ...newProperty, address: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Property Value</Label>
                  <Input
                    type="number"
                    value={newProperty.value || ''}
                    onChange={(e) => setNewProperty({ ...newProperty, value: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mortgage Balance</Label>
                  <Input
                    type="number"
                    value={newProperty.mortgageBalance || ''}
                    onChange={(e) => setNewProperty({ ...newProperty, mortgageBalance: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Mortgage Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newProperty.mortgageRate || ''}
                    onChange={(e) => setNewProperty({ ...newProperty, mortgageRate: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Monthly Payment</Label>
                  <Input
                    type="number"
                    value={newProperty.monthlyPayment || ''}
                    onChange={(e) => setNewProperty({ ...newProperty, monthlyPayment: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Appreciation (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newProperty.appreciation || ''}
                    onChange={(e) => setNewProperty({ ...newProperty, appreciation: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              {(newProperty.type === 'rental' || newProperty.type === 'commercial') && (
                <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                  <div className="space-y-2">
                    <Label>Monthly Rent</Label>
                    <Input
                      type="number"
                      value={newProperty.rent || ''}
                      onChange={(e) => setNewProperty({ ...newProperty, rent: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Monthly Expenses</Label>
                    <Input
                      type="number"
                      value={newProperty.expenses || ''}
                      onChange={(e) => setNewProperty({ ...newProperty, expenses: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Rent Growth (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={newProperty.rentGrowth || ''}
                      onChange={(e) => setNewProperty({ ...newProperty, rentGrowth: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddProperty}>Add Property</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Value"
          value={formatCurrency(realEstate.totalValue)}
          icon={Building2}
        />
        <StatCard
          title="Total Equity"
          value={formatCurrency(realEstate.totalEquity)}
          icon={Home}
          changeType="positive"
        />
        <StatCard
          title="Mortgage Debt"
          value={formatCurrency(realEstate.totalMortgageDebt)}
          icon={Building2}
          changeType="negative"
        />
        <StatCard
          title="Annual Rental Income"
          value={formatCurrency(realEstate.totalRentalIncome)}
          icon={Building2}
          changeType={realEstate.totalRentalIncome > 0 ? 'positive' : 'neutral'}
        />
      </div>

      {/* Properties Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {realEstate.properties.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="text-center py-8 text-muted-foreground">
              <p>No properties yet. Add your first property to get started.</p>
            </CardContent>
          </Card>
        ) : (
          realEstate.properties.map((property) => (
            <Card key={property.id}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    {getPropertyTypeIcon(property.type)}
                    <div>
                      <CardTitle className="text-lg">{property.name}</CardTitle>
                      <Badge variant="secondary" className="capitalize mt-1">
                        {property.type.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveProperty(property.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {property.address && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    {property.address}
                  </div>
                )}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Value</span>
                    <span className="font-medium">{formatCurrency(property.value)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Mortgage</span>
                    <span className="font-medium text-destructive">{formatCurrency(property.mortgageBalance)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Equity</span>
                    <span className="font-medium text-success">{formatCurrency(property.equity)}</span>
                  </div>
                  {property.monthlyPayment > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Monthly Payment</span>
                      <span className="font-medium">{formatCurrency(property.monthlyPayment)}</span>
                    </div>
                  )}
                  {(property.type === 'rental' || property.type === 'commercial') && property.rent > 0 && (
                    <>
                      <div className="border-t pt-2 mt-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Monthly Rent</span>
                          <span className="font-medium text-success">{formatCurrency(property.rent)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Expenses</span>
                          <span className="font-medium">{formatCurrency(property.expenses)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Net Income</span>
                          <span className={`font-medium ${property.rent - property.expenses > 0 ? 'text-success' : 'text-destructive'}`}>
                            {formatCurrency(property.rent - property.expenses)}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  )
}
